import { makeStyles, Theme, createStyles, Box } from "@material-ui/core";
import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import ProductApi from "../../../api/services/ProductApi";
import CustomButton from "../../../components/Button/CustomButton";
import CompetitorProductEditCreateDialog from "./CompetitorProductEditCreateDialog";
import CompetitorCompanyCreateDialog from "./CompetitorCompanyCreateDialog";
import CompetitorProductOverviewTable from "./CompetitorProductOverviewTable";
import { tesaWhite } from "../../../styles/colors";
import useEnv from "../../../hooks/env";
import PagedResponse from "../../../api/models/PagedResponse";
import { CustomTableRowData } from "../../../components/CustomTable";
import { GalleryContextProvider } from "../../../contexts/GalleryContext";
import { OTHER, PICTURE, TDS } from "../../../components/Gallery/model/GalleryFlag.model";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
    },
    link: {
      color: tesaWhite,
      fontFamily: "Proxima Nova Bold",
      textDecoration: "none",
      "&:hover": {
        color: "rgb(70, 71, 117)",
      },
    },
    marginBottomM: {
      marginBottom: theme.spacing(3),
    },
    marginBottomL: {
      marginBottom: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(4),
    },
  }),
);

const CompetitorProductDbOverview = () => {
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const history = useHistory();
  const classes = useStyles();
  const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);
  const [isAddCompanyDialogOpen, setIsAddCompanyDialogOpen] = useState(false);
  const [isEditableProduct, setIsEditableProduct] = useState(true);
  const [competitorProductInitialData, setCompetitorProductInitialData] = useState<CompetitorProduct>();
  const [competitorProductPagedRes, setCompetitorProductPagedRes] = useState<
    PagedResponse<CompetitorProduct> | undefined
  >();
  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const recentReqId = localStorage.getItem("recentReqId");
  const recentTab = localStorage.getItem("recentTab");
  const recentDialog = localStorage.getItem("recentDialog");

  const updateProducts = () => {
    productApi
      .getCompetitorProduct()
      .then(({ data }) => {
        setCompetitorProductPagedRes(data);
      })
      .catch(handleHttpError);
  };

  const updateSelectedProduct = (product: CompetitorProduct) => {
    setCompetitorProductInitialData(product);
  };

  const handleCompanyDialogClose = (doUpdate: boolean) => {
    setIsAddCompanyDialogOpen(false);
    if (doUpdate) {
      updateProducts();
    }
  };

  const handleProductDialogClose = (doUpdate: boolean) => {
    setIsAddProductDialogOpen(false);
    if (doUpdate) {
      updateProducts();
    }
  };

  const handleEdit = (row: CustomTableRowData) => {
    productApi.isEditableCompetitorProduct(row.id).then(({ data: isEditable }) => {
      if (isEditable !== isEditableProduct) {
        setIsEditableProduct(isEditable);
      }
      setCompetitorProductInitialData(competitorProductPagedRes?.data.find((elm) => elm.id === row.id));
      setIsAddProductDialogOpen(true);
    });
  };

  return (
    <div className={classes.root}>
      <h2>Competitor Product Database</h2>
      <Box className={classes.marginBottomL} />
      <div>
        <CustomButton
          className={[classes.marginBottomM, classes.marginRight].join(" ")}
          add
          onClick={() => {
            setIsAddProductDialogOpen(true);
          }}
          text="Create Competitor Product"
        />
        {recentReqId && recentTab ? (
          <CustomButton
            className={classes.marginBottomM}
            onClick={() => {
              const recentReqIdFreeze = recentReqId;
              localStorage.removeItem("recentReqId");
              history.push(`/requests/${recentReqIdFreeze}/${recentTab}${recentDialog && `/${recentDialog}`}`);
            }}
            text="Back to Request"
          />
        ) : (
          <></>
        )}
      </div>
      <CompetitorProductOverviewTable
        competitorProductPagedRes={competitorProductPagedRes}
        setCompetitorProductPagedRes={setCompetitorProductPagedRes}
        handleEdit={handleEdit}
      />
      <GalleryContextProvider flags={[PICTURE, TDS, OTHER]}>
        <CompetitorProductEditCreateDialog
          exitedCallback={() => setCompetitorProductInitialData(undefined)}
          initialData={competitorProductInitialData}
          readonly={!isEditableProduct}
          openCreateCompanyDialog={() => {
            setIsAddCompanyDialogOpen(true);
          }}
          open={isAddProductDialogOpen}
          onClose={handleProductDialogClose}
          updateInitialData={updateSelectedProduct}
        />
      </GalleryContextProvider>
      <CompetitorCompanyCreateDialog open={isAddCompanyDialogOpen} onClose={handleCompanyDialogClose} />
    </div>
  );
};

export default CompetitorProductDbOverview;
